import { BooleanFeature, FeatureGrants, NumericFeature } from "../feature";

export type TierDef = {
  order: number;
  userName:
    | "Enterprise"
    | "Free"
    | "Standard"
    | "Pro"
    | "Team"
    | "Audience"
    | "Business"
    | "Basic"
    | "Custom"
    | "Pro (Legacy)";
  features: FeatureGrants[];
};

export enum Tier {
  FREE = "FREE",
  BASIC = "BASIC",
  PRO = "PRO",
  UL_TEAM = "UL_TEAM",
  ENTERPRISE = "ENTERPRISE",
  MANAGED_TRIAL_ORG = "MANAGED_TRIAL_ORG",
  MANAGED_TRIAL_USER = "MANAGED_TRIAL_USER",
  TEAM = "TEAM",
  AUDIENCE = "AUDIENCE",
  BUSINESS = "BUSINESS",
  CUSTOM = "CUSTOM",
}

// Team plan seat floor. Shared by selfServePlanInfo.licensesMin and the subscription
// model guards (createOrReplaceUserSubscription, updateUserSubscription).
export const UL_TEAM_MIN_LICENSES = 5;

// the tiers in order from worst to best
export const USER_LEVEL_TIERS = Object.freeze([
  Tier.MANAGED_TRIAL_USER,
  Tier.BASIC,
  Tier.AUDIENCE,
  Tier.PRO,
  Tier.UL_TEAM,
  Tier.ENTERPRISE,
  Tier.CUSTOM,
]);
export const ORG_LEVEL_TIERS = Object.freeze([
  Tier.FREE,
  Tier.ENTERPRISE,
  Tier.MANAGED_TRIAL_ORG,
  Tier.TEAM,
  Tier.BUSINESS,
  Tier.CUSTOM,
]);

export const ALL_TIERS = Object.freeze([
  ...USER_LEVEL_TIERS,
  ...ORG_LEVEL_TIERS,
]);

const free_2025: TierDef = {
  order: 0,
  userName: "Free",
  features: [
    {
      [BooleanFeature.DisableAnonymity]: true,
      [BooleanFeature.DisablePremiumPolly]: true,
      [BooleanFeature.DisablePrivateResults]: true,
      [BooleanFeature.DisableResultsCollaborators]: true,
      [BooleanFeature.DisableSchedulingOptions]: true,
      [BooleanFeature.DisableTemplateSharing]: true,
      [BooleanFeature.DisableAdvanceQnA]: true,

      [BooleanFeature.HideResponseLimits]: true,
      [BooleanFeature.AdvancedShareControls]: true,
      [BooleanFeature.SlackMCP]: true,
      [BooleanFeature.SlashPollyShare]: true,

      [NumericFeature.DaysOfHistory]: 45,
      [NumericFeature.DirectMessageAudienceLimit]: 1,
      [NumericFeature.ResponsesPerMonth]: Infinity,
    },
  ],
};

const basic_2025: TierDef = {
  order: 0,
  userName: "Basic",
  features: [
    {
      [BooleanFeature.DisableAdvanceQnA]: true,
      [BooleanFeature.DisableAnonymity]: true,
      [BooleanFeature.DisablePrivateResults]: true,
      [BooleanFeature.DisableResultsCollaborators]: true,
      [BooleanFeature.DisableTemplateSharing]: true,

      [BooleanFeature.AccessSharedItems]: true,
      [BooleanFeature.AdvancedShareControls]: true,
      [BooleanFeature.ExportQuestion]: true,
      [BooleanFeature.HideResponseLimits]: true,
      [BooleanFeature.PaidTier]: true,
      [BooleanFeature.ImagePolly]: true,
      [BooleanFeature.QnAv2]: true,
      [BooleanFeature.SlackMCP]: true,
      [BooleanFeature.SlashPollyExport]: true,
      [BooleanFeature.SlashPollyShare]: true,
      [BooleanFeature.TrendHistory]: true,
      [BooleanFeature.WordCloud]: true,

      [NumericFeature.DaysOfHistory]: Infinity,
      [NumericFeature.DirectMessageAudienceLimit]: 1,
      [NumericFeature.ResponsesPerMonth]: Infinity,
    },
  ],
};

const pro_2025: TierDef = {
  order: 0,
  userName: "Pro",
  features: [
    {
      [BooleanFeature.AccessSharedItems]: true,
      [BooleanFeature.AdvancedShareControls]: true,
      [BooleanFeature.AuthenticatedWebLinks]: true,
      [BooleanFeature.EventsGA]: true,
      [BooleanFeature.ExportQuestion]: true,
      [BooleanFeature.GoogleSheetsSync]: true,
      [BooleanFeature.HideResponseLimits]: true,
      [BooleanFeature.ImagePolly]: true,
      [BooleanFeature.PaidTier]: true,
      [BooleanFeature.QnAv2]: true,
      [BooleanFeature.Quiz]: true,
      [BooleanFeature.SlackMCP]: true,
      [BooleanFeature.SlashPollyExport]: true,
      [BooleanFeature.SlashPollyShare]: true,
      [BooleanFeature.Themes]: true,
      [BooleanFeature.TrendHistory]: true,
      [BooleanFeature.WordCloud]: true,

      [NumericFeature.DaysOfHistory]: Infinity,
      [NumericFeature.DirectMessageAudienceLimit]: Infinity,
      [NumericFeature.ResponsesPerMonth]: Infinity,
    },
  ],
};

// Team plan: Pro features + Demographics (via Analytics) + Workflows. 5-seat minimum.
const ul_team_2026: TierDef = {
  order: 10,
  userName: "Team",
  features: [
    ...pro_2025.features,
    {
      [BooleanFeature.Analytics]: true,
      [BooleanFeature.Workflows]: true,
      // Team plan exposes audience-by-email parity with the marketing copy on
      // the V2 pricing card — previously this entitlement was Enterprise-only.
      [BooleanFeature.AddAudienceByEmailList]: true,
    },
  ],
};

const standard: TierDef = {
  order: 10,
  userName: "Standard",
  features: [
    {
      [BooleanFeature.AccessSharedItems]: true,
      [BooleanFeature.AdvancedShareControls]: true,
      [BooleanFeature.ExportQuestion]: true,
      [BooleanFeature.PaidTier]: true,
      [BooleanFeature.SlashPollyExport]: true,
      [BooleanFeature.SlashPollyShare]: true,
      [BooleanFeature.TrendHistory]: true,
      [NumericFeature.DaysOfHistory]: Infinity,
      [NumericFeature.DirectMessageAudienceLimit]: Infinity,
      [NumericFeature.ResponsesPerMonth]: Infinity,
    },
  ],
};
const proLegacy: TierDef = {
  order: 25,
  userName: "Pro (Legacy)",
  features: [
    ...standard.features,
    {
      [BooleanFeature.Analytics]: true,
      [BooleanFeature.Api]: true,
      [BooleanFeature.GoogleSheetsSync]: true,
      [NumericFeature.ResponsesPerMonth]: Infinity,
    },
  ],
};

const team: TierDef = {
  order: 35,
  userName: "Team",
  features: [
    {
      [BooleanFeature.AccessSharedItems]: true,
      [BooleanFeature.AuthenticatedWebLinks]: true,
      [BooleanFeature.AdvancedShareControls]: true,
      [BooleanFeature.EventsGA]: true,
      [BooleanFeature.ExportQuestion]: true,
      [BooleanFeature.GoogleSheetsSync]: true,
      [BooleanFeature.HideResponseLimits]: true,
      [BooleanFeature.ImagePolly]: true,
      [BooleanFeature.PaidTier]: true,
      [BooleanFeature.QnAv2]: true,
      [BooleanFeature.Quiz]: true,
      [BooleanFeature.SlackMCP]: true,
      [BooleanFeature.SlashPollyExport]: true,
      [BooleanFeature.SlashPollyShare]: true,
      [BooleanFeature.Themes]: true,
      [BooleanFeature.TrendHistory]: true,
      [BooleanFeature.WordCloud]: true,
      [BooleanFeature.Analytics]: true,
      [BooleanFeature.PollyPairing]: true,
      [NumericFeature.DaysOfHistory]: Infinity,
      [NumericFeature.DirectMessageAudienceLimit]: Infinity,
      [NumericFeature.ResponsesPerMonth]: Infinity,
    },
  ],
};

const custom: TierDef = {
  order: 35,
  userName: "Team",
  features: [
    {
      [BooleanFeature.AccessSharedItems]: true,
      [BooleanFeature.AdvancedShareControls]: true,
      [BooleanFeature.HideResponseLimits]: true,
      [BooleanFeature.PaidTier]: true,
      [BooleanFeature.SlashPollyShare]: true,
      [BooleanFeature.TrendHistory]: true,
      [NumericFeature.DaysOfHistory]: Infinity,
      [NumericFeature.ResponsesPerMonth]: Infinity,
    },
  ],
};

/**
 * Another tier's grants, less Presentations (Events GA). A grant counts whenever its key is
 * present, so a tier copying one that has it can only leave it out, not switch it off.
 */
const withoutEventsGA = (features: FeatureGrants[]): FeatureGrants[] =>
  features.map((grants) => {
    const copy = { ...grants };
    delete copy[BooleanFeature.EventsGA];
    return copy;
  });

const audience: TierDef = {
  order: 35,
  userName: "Team",
  features: withoutEventsGA(team.features),
};

const business: TierDef = {
  order: 40,
  userName: "Business",
  features: [
    ...proLegacy.features,
    {
      // Borrowed from Enterprise
      [BooleanFeature.EnterpriseAdminDashboard]: true,
      [BooleanFeature.EnterpriseUsagePage]: true,
      [BooleanFeature.EnterpriseUserManagement]: true,
      [NumericFeature.ResponsesPerMonth]: Infinity,
      [NumericFeature.DirectMessageAudienceLimit]: Infinity,
      [BooleanFeature.AuthenticatedWebLinks]: true,
      [BooleanFeature.ImagePolly]: true,
      [BooleanFeature.Quiz]: true,
      [BooleanFeature.Themes]: true,
      [BooleanFeature.WordCloud]: true,

      // Additional features
      [BooleanFeature.QnAv2]: true,
      [BooleanFeature.ManageSuggestionBox]: true,
      [BooleanFeature.SlackMCP]: true,
      [BooleanFeature.Workflows]: true,
      [BooleanFeature.PollyPairing]: true,
      [BooleanFeature.EventsGA]: true,
    },
  ],
};

const enterprise: TierDef = {
  order: 45,
  userName: "Enterprise",
  features: [
    ...proLegacy.features,
    {
      [BooleanFeature.AuthenticatedWebLinks]: true,
      [BooleanFeature.AddAudienceByEmailList]: true,
      [BooleanFeature.EnterpriseActivityLogs]: true,
      [BooleanFeature.EnterpriseAdminDashboard]: true,
      [BooleanFeature.EnterpriseSettingsDataRetention]: true,
      [BooleanFeature.EnterpriseUserManagement]: true,
      [BooleanFeature.ImagePolly]: true,
      [BooleanFeature.QnAv2]: true,
      [BooleanFeature.Quiz]: true,
      [BooleanFeature.Themes]: true,
      [BooleanFeature.WordCloud]: true,

      [NumericFeature.DirectMessageAudienceLimit]: Infinity,
      [NumericFeature.ResponsesPerMonth]: Infinity,
    },
  ],
};
const managedTrialOrganization: TierDef = {
  order: 0,
  userName: "Free",
  features: [
    ...free_2025.features,
    {
      [BooleanFeature.RequestManagedTrialLicense]: true,
    },
  ],
};
const managedTrialUser: TierDef = {
  order: 5,
  userName: "Pro",
  features: [
    ...withoutEventsGA(pro_2025.features),
    {
      [BooleanFeature.ManagedTrialLicense]: true,
    },
  ],
};

export const tierDefs: { [key in Tier]: TierDef } = {
  [Tier.FREE]: free_2025,
  [Tier.BASIC]: basic_2025,
  [Tier.PRO]: pro_2025,
  [Tier.UL_TEAM]: ul_team_2026,
  [Tier.ENTERPRISE]: enterprise,
  [Tier.MANAGED_TRIAL_ORG]: managedTrialOrganization,
  [Tier.MANAGED_TRIAL_USER]: managedTrialUser,
  [Tier.TEAM]: team,
  [Tier.AUDIENCE]: audience,
  [Tier.BUSINESS]: business,
  [Tier.CUSTOM]: custom,
};

const enterprise_level_plans = [
  "Enterprise",
  "Team",
  "Audience",
  "Business",
  "Custom",
];

export function isEnterpriseTypePlan(
  planUserName: TierDef["userName"],
): boolean {
  return enterprise_level_plans.includes(planUserName);
}
